import React from 'react';
import { Button, Typography } from 'antd';
import type { CorrectionEligibilityViewV0 } from '@shared/operator-read-models';
import { FAKE_UNDO_FORBIDDEN_V0, type RecoveryActionBindingV0 } from '../recoveryGrammar';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Paragraph } = Typography;

export interface RecoveryPanelProps {
  readonly presentationState: PrimitivePresentationState;
  /** R0 CorrectionEligibilityView — no client-side eligibility invention. */
  readonly eligibility?: CorrectionEligibilityViewV0 | null;
  readonly recoveryActions?: readonly RecoveryActionBindingV0[];
  readonly onRecoveryAction?: (kind: RecoveryActionBindingV0['kind']) => void;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function RecoveryPanel(props: RecoveryPanelProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('RecoveryPanel must keep grantsAuthority/grantsApproval=false');
  }
  if (FAKE_UNDO_FORBIDDEN_V0 !== true) {
    throw new Error('RecoveryPanel requires FAKE_UNDO_FORBIDDEN_V0');
  }

  const eligibility = props.eligibility;
  const actions = (props.recoveryActions ?? []).filter((action) => {
    if (action.fakeUndo !== false) {
      throw new Error('RecoveryPanel forbids fakeUndo bindings');
    }
    return action.available;
  });

  const canOfferRecovery = eligibility?.eligibility === 'ELIGIBLE';

  return (
    <PrimitiveSurfaceFrame
      title="복구"
      presentationState={props.presentationState}
      emptyMessage="복구 적격성 정보가 없습니다."
      deniedMessage="복구가 허용되지 않습니다."
      data-testid={props['data-testid'] ?? 'recovery-panel'}
      aria-label="복구 패널"
    >
      <div className={styles.primitiveStack}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          FAKE_UNDO_FORBIDDEN_V0=true · 클라이언트 적격성 발명 금지
        </Paragraph>
        {eligibility ? (
          <>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>eligibility (서버)</span>
              <Text strong data-eligibility={eligibility.eligibility}>
                {eligibility.eligibility}
              </Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>원본 영수증</span>
              <Text>{eligibility.originalReceiptId}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>허용 복구 액션</span>
              <Text>{eligibility.allowedRecoveryAction ?? '—'}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>안전 다음 단계</span>
              <Text>{eligibility.safeNextAction ?? '—'}</Text>
            </div>
            {eligibility.blockingReasons.length > 0 ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>차단 사유</span>
                <Text>{eligibility.blockingReasons.join('; ')}</Text>
              </div>
            ) : null}
            {eligibility.stateMayHaveChanged ? (
              <Text type="warning">상태가 변경되었을 수 있습니다.</Text>
            ) : null}
          </>
        ) : (
          <Text type="secondary">CorrectionEligibilityView 없음</Text>
        )}
        {actions.length > 0 ? (
          <div className={styles.primitiveActionStack}>
            {actions.map((action) => (
              <Button
                key={action.kind}
                onClick={() => props.onRecoveryAction?.(action.kind)}
                disabled={!canOfferRecovery || !action.runtimeOperationId}
                data-testid={`recovery-action-${action.kind}`}
              >
                {action.labelKo}
              </Button>
            ))}
          </div>
        ) : null}
        {!canOfferRecovery ? (
          <Text type="secondary">
            서버 ELIGIBLE가 아니면 복구 액션을 비활성화합니다 (FAKE_UNDO_FORBIDDEN_V0).
          </Text>
        ) : null}
      </div>
    </PrimitiveSurfaceFrame>
  );
}
